/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   UserProvider.tsx
 *     Description     :   Loads the current AppUser (role + preferences) once authenticated, applies
 *                         the user's saved theme to the design system, and exposes the user + role to
 *                         the tree via useCurrentUser. DB-backed roles (Administrator/Operator/Viewer).
 *     Module          :   user-management (frontend/auth)
 *     Component Type  :   Provider
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-25      Peter Dobler        Initial creation (user + role + preferences)
 * -----------------------------------------------------------------------------------------
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     UserProvider, useCurrentUser
 *
 * =========================================================================================
 */

import { createContext, useContext, useEffect, type ReactNode } from "react";
import type { AppUser } from "@nextgenmigration/shared/user";
import { useAuth } from "./useAuth.js";
import { isDevBypass } from "./token.js";
import { useDevRole } from "./devRole.js";
import { useMeQuery } from "../api/user.js";
import { useTheme } from "../theme/index.js";

export interface UserContextValue {
  /** The current app user (role + preferences), or null until loaded / when unauthenticated. */
  user: AppUser | null;
  /** True while the user record is loading. */
  isLoading: boolean;
  /** Convenience: the caller holds the Administrator role. */
  isAdmin: boolean;
}

export const UserContext = createContext<UserContextValue>({
  user: null,
  isLoading: false,
  isAdmin: false,
});

/** Provides the current AppUser + role and applies the saved theme. */
export function UserProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { isAuthenticated } = useAuth();
  const { setMode } = useTheme();
  const { data, isLoading } = useMeQuery(isAuthenticated);
  const devRole = useDevRole((s) => s.role);

  // Apply the user's saved theme preference to the design system when it loads/changes.
  const theme = data?.preferences.theme;
  useEffect(() => {
    if (theme) setMode(theme);
  }, [theme, setMode]);

  // Under DEV_AUTH_BYPASS a local role override (the dev role selector) takes effect so role-gated
  // UI can be tested without real Entra assignments. Ignored entirely in real/prod auth.
  const effectiveRole = isDevBypass() && devRole ? devRole : data?.role;
  const user: AppUser | null = data ? { ...data, role: effectiveRole ?? data.role } : null;

  const value: UserContextValue = {
    user,
    isLoading,
    isAdmin: user?.role === "Administrator",
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/** Returns the current user context (user, loading, isAdmin). */
export function useCurrentUser(): UserContextValue {
  return useContext(UserContext);
}
